/**
 * SessionStorage key used to track the user's registration email during onboarding flow.
 * Set after registration, checked during onboarding, and cleared after completion.
 */
export const REGISTRATION_EMAIL_KEY = 'registration-email';

/**
 * SessionStorage key used to track the user's name during onboarding flow.
 * Set after registration, used for personalization, and cleared after completion.
 */
export const REGISTRATION_NAME_KEY = 'registration-name';

/**
 * Sets the onboarding session data in sessionStorage.
 * Should be called after successful registration to start the onboarding flow.
 *
 * @param email - User's registration email address
 * @param name - User's full name (firstName + lastName) for personalization
 *
 * @example
 * ```tsx
 * // After successful registration
 * const fullName = `${values.firstName} ${values.lastName}`.trim();
 * setOnboardingSession(values.email, fullName);
 * navigate('/onboarding', { replace: true });
 * ```
 */
export const setOnboardingSession = (email: string, name?: string): void => {
  try {
    sessionStorage.setItem(REGISTRATION_EMAIL_KEY, email);
    if (name) {
      sessionStorage.setItem(REGISTRATION_NAME_KEY, name);
    } else {
      sessionStorage.removeItem(REGISTRATION_NAME_KEY);
    }
  } catch (error) {
    console.warn('Failed to set onboarding session in sessionStorage:', error);
    // Swallow error to prevent blocking registration flow
  }
};

/**
 * Clears the onboarding session from sessionStorage.
 * Should be called after the user completes onboarding and email verification.
 * Handles sessionStorage errors gracefully.
 */
export const clearOnboardingSession = (): void => {
  try {
    sessionStorage.removeItem(REGISTRATION_EMAIL_KEY);
    sessionStorage.removeItem(REGISTRATION_NAME_KEY);
  } catch (error) {
    console.warn('Failed to clear onboarding session from sessionStorage:', error);
    // Swallow error to prevent blocking user flow
  }
};
